import type * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { DropdownMenu } from '@buffer-mono/popcorn'

import type { Service } from '~publish/pages/Community/graphqlEnums'

import { ResolveCommentsDialog } from '../ResolveCommentsDialog'
import { ResolveMentionsDialog } from '../ResolveMentionsDialog'
import type { DismissActionType } from '../types'
import { BulkActionsMenuContext } from './BulkActionsMenuContext'
import type {
  BulkActionsEntity,
  BulkActionsMenuContextValue,
} from './BulkActionsMenuContext'

export type BulkActionsMenuRootProps = {
  children: React.ReactNode
  channelIds: string[]
  channelService?: Service
  postId?: string
  entity?: BulkActionsEntity
  onResolvingChange?: (isResolving: boolean) => void
}

export function BulkActionsMenuRoot({
  children,
  channelIds,
  channelService,
  postId,
  entity = 'comments',
  onResolvingChange,
}: BulkActionsMenuRootProps): JSX.Element {
  const [dialogState, setDialogState] = useState<{
    shouldOpen: boolean
    actionType: DismissActionType
  }>({
    shouldOpen: false,
    actionType: 'all',
  })

  const requestResolve = useCallback((actionType: DismissActionType): void => {
    // Both comments and mentions open a confirmation dialog; the dialog
    // rendered below is chosen by `entity`.
    setDialogState({ shouldOpen: true, actionType })
  }, [])

  const handleDialogOpenChange = useCallback((open: boolean): void => {
    if (!open) {
      setDialogState((prev) => ({ ...prev, shouldOpen: false }))
    }
  }, [])

  const contextValue = useMemo<BulkActionsMenuContextValue>(
    () => ({
      channelIds,
      channelService,
      postId,
      entity,
      onResolvingChange,
      requestResolve,
    }),
    [
      channelIds,
      channelService,
      postId,
      entity,
      onResolvingChange,
      requestResolve,
    ],
  )

  return (
    <BulkActionsMenuContext.Provider value={contextValue}>
      <DropdownMenu modal={false}>{children}</DropdownMenu>
      {entity === 'comments' && (
        <ResolveCommentsDialog
          channelIds={channelIds}
          postId={postId}
          actionType={dialogState.actionType}
          shouldOpen={dialogState.shouldOpen}
          onOpenChange={handleDialogOpenChange}
          onResolvingChange={onResolvingChange}
        />
      )}
      {entity === 'mentions' && (
        <ResolveMentionsDialog
          channelIds={channelIds}
          actionType={dialogState.actionType}
          shouldOpen={dialogState.shouldOpen}
          onOpenChange={handleDialogOpenChange}
          onResolvingChange={onResolvingChange}
        />
      )}
    </BulkActionsMenuContext.Provider>
  )
}
